import { Controller, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout';
import { Typography } from 'components/typography';
import useTMSContext from 'hooks/useTMSContext';
import {
  EQUIPMENT_PLACEHOLDER,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { NormalizedLoad } from 'types/Load';
import { TMS } from 'types/enums/Integrations';

import { STARK_EQUIPMENT_OPTIONS } from './utils';

export function SpecificationsForm({
  formMethods,
  isCreateMode = false,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  isCreateMode?: boolean;
}) {
  const {
    control,
    formState: { errors },
  } = formMethods;
  const { tmsName } = useTMSContext();

  // Hide Volume field for Revenova and Stark TMS in create mode or always for Revenova/Stark
  const isVolumeSupported =
    tmsName !== TMS.Revenova && tmsName !== TMS.Stark && !isCreateMode;

  // Hide Distance field during create mode
  const isDistanceSupported = !isCreateMode;

  return (
    <Grid cols='1' gap='md' className='w-full mx-0'>
      {/* Mode field - like LoadInformationTab */}
      <div className='space-y-1'>
        <Label name='mode' required={isCreateMode}>
          Mode
        </Label>
        <Controller
          name='mode'
          control={control}
          rules={isCreateMode ? { required: 'Mode is required' } : {}}
          render={({ field }) => (
            <Select
              value={field.value || 'TL'}
              onValueChange={field.onChange}
              disabled
            >
              <SelectTrigger>
                <SelectValue placeholder='Select mode' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='TL'>Truckload</SelectItem>
                <SelectItem value='LTL'>LTL</SelectItem>
                <SelectItem value='Refrigerated'>Refrigerated</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <div className='space-y-1'>
        <RHFTextInput
          name='specifications.commodities'
          label='Commodity Description'
          readOnly={true}
        />
      </div>

      <div className='space-y-1'>
        {isCreateMode ? (
          <>
            <Label name='specifications.transportType' required={true}>
              Transport Type
            </Label>
            <Controller
              name='specifications.transportType'
              control={control}
              rules={{ validate: validateEquipmentRequired }}
              render={({ field }) => (
                <Select
                  name={field.name}
                  placeholder={EQUIPMENT_PLACEHOLDER}
                  value={field.value || undefined}
                  onChange={field.onChange}
                  options={STARK_EQUIPMENT_OPTIONS}
                  data-testid='stark-equipment-select'
                />
              )}
            />
            <ErrorMessage
              errors={errors}
              name='specifications.transportType'
              render={({ message }: { message: string }) => (
                <Typography variant='body-xs' className='text-error-500'>
                  {message}
                </Typography>
              )}
            />
          </>
        ) : (
          <RHFTextInput
            name='specifications.transportType'
            label='Transport Type'
            readOnly={true}
          />
        )}
      </div>

      {/* Volume - hidden for Revenova TMS */}
      {isVolumeSupported && (
        <RHFTextInput
          name='specifications.totalVolume.val'
          label='Volume'
          inputType='number'
          options={{
            min: {
              value: 0,
              message: 'Volume must be greater than 0',
            },
            valueAsNumber: true,
          }}
        />
      )}

      {/* Distance and Pallets row */}
      <Grid cols='3' gap='sm' className='w-full'>
        {/* Weight */}
        <RHFTextInput
          required={isCreateMode}
          name='specifications.totalWeight.val'
          label='Weight (lbs)'
          inputType='number'
          options={{
            ...(isCreateMode && { required: 'Weight is required' }),
            min: {
              value: 0,
              message: 'Weight cannot be negative',
            },
            valueAsNumber: true,
          }}
        />
        {isDistanceSupported && (
          <RHFTextInput
            name='specifications.totalDistance.val'
            label='Distance (mi)'
            inputType='number'
            readOnly
            options={{
              min: {
                value: 0,
                message: 'Distance must be greater than 0',
              },
              valueAsNumber: true,
            }}
          />
        )}

        <RHFTextInput
          required={isCreateMode}
          name='specifications.totalOutPalletCount'
          label='Pallets'
          inputType='number'
          options={{
            ...(isCreateMode && { required: 'Pallets is required' }),
            min: {
              value: 0,
              message: 'Pallets cannot be negative',
            },
            valueAsNumber: true,
          }}
        />
      </Grid>
    </Grid>
  );
}
